import React from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../../api/auth';
import logo from '../../public/images/Leikin_Team_Logo.png';

const AnimatedLoginForm = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [appleReady, setAppleReady] = React.useState(false);

  const appleClientId =
    process.env.REACT_APP_APPLE_SERVICE_ID ||
    process.env.REACT_APP_APPLE_CLIENT_ID ||
    '';
  const appleRedirectUri =
    process.env.REACT_APP_APPLE_REDIRECT_URI ||
    `${window.location.origin}/auth`;

  React.useEffect(() => {
    if (!appleClientId) {
      setAppleReady(false);
      return;
    }

    const initAppleAuth = () => {
      if (!window.AppleID?.auth) {
        return;
      }

      window.AppleID.auth.init({
        clientId: appleClientId,
        scope: 'name email',
        redirectURI: appleRedirectUri,
        usePopup: true,
      });
      setAppleReady(true);
    };

    if (window.AppleID?.auth) {
      initAppleAuth();
      return;
    }

    const existingScript = document.querySelector('script[data-apple-signin="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', initAppleAuth);
      return () => existingScript.removeEventListener('load', initAppleAuth);
    }

    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.defer = true;
    script.dataset.appleSignin = 'true';
    script.addEventListener('load', initAppleAuth);
    document.body.appendChild(script);

    return () => {
      script.removeEventListener('load', initAppleAuth);
    };
  }, [appleClientId, appleRedirectUri]);

  const parseEnabledProviders = () => {
    const raw = process.env.REACT_APP_ENABLED_SOCIAL_PROVIDERS || 'google,apple';
    return new Set(
      raw
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean)
    );
  };
  const enabledProviders = parseEnabledProviders();

  const providers = [
    {
      id: 'google',
      title: 'Google',
      icon: (
        <svg className="w-7 h-7 sm:w-9 sm:h-9" viewBox="0 0 24 24">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
    },
    {
      id: 'facebook',
      title: 'Facebook',
      icon: (
        <svg className="w-7 h-7 sm:w-9 sm:h-9" fill="#1877F2" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      ),
    },
    {
      id: 'apple',
      title: 'Apple',
      icon: (
        <svg className="w-7 h-7 sm:w-9 sm:h-9" fill="#000" viewBox="0 0 24 24">
          <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
        </svg>
      ),
    },
  ];

  const visibleProviders = providers.filter((provider) => enabledProviders.has(provider.id));

  const completeSocialLogin = React.useCallback((result) => {
    if (result && result.access) {
      localStorage.setItem('access_token', result.access);
      if (result.refresh) {
        localStorage.setItem('refresh_token', result.refresh);
      }

      if (result.user) {
        localStorage.setItem('user', JSON.stringify(result.user));
      }

      toast.success('Успешный вход!');
      navigate('/dashboard');
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 500);
      return true;
    }

    toast.error('Не удалось войти. Попробуйте снова.');
    return false;
  }, [navigate]);

  const handleGoogleCallback = async (tokenResponse) => {
    try {
      if (!tokenResponse || !tokenResponse.access_token) {
        toast.error('Не удалось получить токен от Google');
        return;
      }

      const result = await authAPI.googleLogin(tokenResponse.access_token);
      
      completeSocialLogin(result);
    } catch (error) {
      console.error('Google OAuth error:', error);
      toast.error('Ошибка входа через Google');
    }
  };

  const handleSocialLogin = async (provider) => {
    try {
      if (provider === 'google') {
        if (window.google && window.google.accounts && window.google.accounts.oauth2) {
          const tokenClient = window.google.accounts.oauth2.initTokenClient({
            client_id: '823001963558-s7qd0er16u0fsv4v9mec4dlue0vovh9u.apps.googleusercontent.com',
            scope: 'email profile',
            callback: handleGoogleCallback,
          });
          
          tokenClient.requestAccessToken({ prompt: 'consent' });
        } else {
          toast.error('Google OAuth не загружен. Обновите страницу.');
        }
        return;
      }

      if (provider === 'apple') {
        if (!appleClientId) {
          toast.error('Apple Sign In не настроен для сайта');
          return;
        }

        if (!window.AppleID?.auth || !appleReady) {
          toast.error('Apple Sign In ещё загружается. Повторите через пару секунд.');
          return;
        }

        const response = await window.AppleID.auth.signIn();
        const code = response?.authorization?.code;
        const idToken = response?.authorization?.id_token;

        if (!code && !idToken) {
          toast.error('Не удалось получить данные авторизации Apple');
          return;
        }

        const result = await authAPI.appleWebLogin({ code, idToken });
        completeSocialLogin(result);
        return;
      }

      toast.info(`Вход через ${provider} будет доступен в ближайшее время`);
    } catch (error) {
      console.error('Social login error:', error);
      if (provider === 'apple' && error?.error === 'popup_closed_by_user') {
        return;
      }
      toast.error(`Ошибка входа через ${provider}`);
    }
  };


  const card = (
    <div className="w-full max-w-2xl">
      <div
        className="relative overflow-hidden rounded-3xl shadow-2xl border border-white/15 p-8 sm:p-10 lg:p-12"
        style={{
          background:
            'linear-gradient(100deg, rgba(84, 69, 128, 0.72) 0%, rgba(74, 127, 180, 0.7) 52%, rgba(63, 171, 201, 0.68) 100%)',
        }}
      >
        <div className="pointer-events-none absolute inset-0 bg-white/6" />
        <div className="max-w-md mx-auto w-full text-center">
          <img
            src={logo}
            alt="Leikin Team"
            className="w-36 h-36 sm:w-44 sm:h-44 object-contain mx-auto mb-4"
          />
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 drop-shadow-sm">Вход</h2>
          <p className="text-white/90 mb-8">
            Войдите через одну из социальных сетей
          </p>

          <div className="flex items-center justify-center gap-4 sm:gap-5 mb-2">
            {visibleProviders.map((provider) => (
              <button
                key={provider.id}
                onClick={() => handleSocialLogin(provider.id)}
                disabled={provider.id === 'apple' && !appleClientId}
                className="flex flex-col items-center justify-center p-3.5 sm:p-4 rounded-xl border-2 border-white/70 bg-white/94 hover:bg-white transition-all duration-300 h-[4.5rem] sm:h-20 w-[4.5rem] sm:w-20 shadow-[0_10px_22px_rgba(0,0,0,0.2)] backdrop-blur-sm"
                title={provider.title}
              >
                <span className="drop-shadow-[0_2px_5px_rgba(0,0,0,0.22)]">{provider.icon}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return card;
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {card}
    </div>
  );
};

export default AnimatedLoginForm;
